import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const INITIAL_FORM = {
  id: "",
  name: "",
  phone: "",
  email: "",
  address: "",
  notes: "",
};

const mapCustomerToForm = (customer) => ({
  ...INITIAL_FORM,
  id: customer?.id || customer?._id || "",
  name: customer?.name || "",
  phone: customer?.contact?.phone || customer?.phone || "",
  email: customer?.contact?.email || customer?.email || "",
  address: customer?.address || "",
  notes: customer?.notes || "",
});

function CustomerDrawer({
  open,
  onClose,
  onSubmit,
  onUpdate,
  saving = false,
  error = "",
  withOverlay = false,
  customer = null,
  mode = "create",
}) {
  const drawerRef = useRef(null);
  const [form, setForm] = useState(INITIAL_FORM);
  const [initialForm, setInitialForm] = useState(INITIAL_FORM);
  const [localError, setLocalError] = useState("");
  const isCreateMode = mode === "create";
  const isReadOnly = mode === "view";
  const isEditMode = mode === "edit";
  const allowEdits = !isReadOnly;
  const isDirty = useMemo(() => JSON.stringify(form) !== JSON.stringify(initialForm), [form, initialForm]);

  const handleChange = (field) => (event) => {
    const value = event?.target?.value ?? "";
    setForm((prev) => ({ ...prev, [field]: value }));
    setLocalError("");
  };

  const validateForm = useCallback(() => {
    if (!form.name.trim()) {
      setLocalError("Customer name is required.");
      return false;
    }

    const email = form.email.trim();
    if (email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      setLocalError("Customer email is invalid.");
      return false;
    }

    return true;
  }, [form]);

  const handleSubmit = useCallback(() => {
    if (isReadOnly) {
      onClose?.();
      return;
    }
    if (!validateForm()) return;
    if (isEditMode) {
      onUpdate?.(form, customer);
      return;
    }
    onSubmit?.(form);
  }, [customer, form, isEditMode, isReadOnly, onClose, onSubmit, onUpdate, validateForm]);

  const handleCloseAttempt = useCallback(() => {
    if (!allowEdits || !isDirty) {
      onClose?.();
      return;
    }

    const shouldSave = window.confirm("You have unsaved changes. Save before closing? Press Cancel to discard.");
    if (shouldSave) {
      handleSubmit();
    } else {
      onClose?.();
    }
  }, [allowEdits, handleSubmit, isDirty, onClose]);

  useEffect(() => {
    if (!open) return undefined;
    if (withOverlay) return undefined;
    const handleClickOutside = (event) => {
      if (!drawerRef.current) return;
      if (drawerRef.current.contains(event.target)) return;
      handleCloseAttempt();
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [handleCloseAttempt, open, withOverlay]);

  useEffect(() => {
    if (open) {
      const nextForm = customer ? mapCustomerToForm(customer) : INITIAL_FORM;
      setForm(nextForm);
      setInitialForm(nextForm);
      setLocalError("");
    }
  }, [open, customer]);

  if (!open) return null;

  const disableSubmit = saving || !form.name.trim();
  const disableUpdate = disableSubmit || !isDirty;

  const content = (
    <aside
      className="create-drawer"
      aria-label={isCreateMode ? "Create customer panel" : "Customer details panel"}
      ref={drawerRef}
    >
      {localError || error ? (
        <div className="inline-alert error">
          <span>{localError || error}</span>
        </div>
      ) : null}

      <div className="drawer-section">
        <h3>Customer Details</h3>

        <div className="drawer-grid">
          <label className="field">
            <span className="field-label">Customer name</span>
            <input
              type="text"
              placeholder="Customer name"
              value={form.name}
              disabled={!allowEdits}
              onChange={handleChange("name")}
            />
          </label>

          <label className="field">
            <span className="field-label">Phone</span>
            <input
              type="tel"
              placeholder="+965 5555 5555"
              value={form.phone}
              disabled={!allowEdits}
              onChange={handleChange("phone")}
            />
          </label>

          <label className="field">
            <span className="field-label">Email</span>
            <input
              type="email"
              placeholder="name@email.com"
              value={form.email}
              disabled={!allowEdits}
              onChange={handleChange("email")}
            />
          </label>

          <label className="field">
            <span className="field-label">Address</span>
            <input
              type="text"
              placeholder="Street, city"
              value={form.address}
              disabled={!allowEdits}
              onChange={handleChange("address")}
            />
          </label>

          <label className="field">
            <span className="field-label">Notes</span>
            <input
              type="text"
              placeholder="Delivery preferences, reminders"
              value={form.notes}
              disabled={!allowEdits}
              onChange={handleChange("notes")}
            />
          </label>
        </div>
      </div>

      <footer className="drawer-footer">
        {isCreateMode || (isEditMode && isDirty) ? (
          <button
            className="primary-btn wide"
            type="button"
            onClick={handleSubmit}
            disabled={isEditMode ? disableUpdate : disableSubmit}
          >
            {saving ? (isEditMode ? "Updating..." : "Creating...") : isEditMode ? "Update customer" : "Create customer"}
          </button>
        ) : null}
        <button className="ghost-btn subtle wide" type="button" onClick={onClose} disabled={saving}>
          {isReadOnly ? "Close" : "Cancel"}
        </button>
      </footer>
    </aside>
  );

  const handleOverlayClick = (event) => {
    if (event.target === event.currentTarget) {
      handleCloseAttempt();
    }
  };

  if (!withOverlay) return content;

  return (
    <div className="modal-overlay solo-overlay" role="dialog" aria-modal="true" onClick={handleOverlayClick}>
      <div className="create-layer solo-drawer">{content}</div>
    </div>
  );
}

export default CustomerDrawer;
